import { Application } from '@invision/core';
import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { rgba } from 'polished';
import { colors } from './constants/colors';
import {
  BASE_SPACING,
  DEFAULT_BUTTON_HEIGHT,
  FONT_SIZE
} from './constants/sizes';
import {
  boxShadowInput,
  boxShadowMedium,
  boxShadowTooltip,
  boxShadowTooltipArrow,
  standardBorder,
  transparentBorder
} from './constants/styles';
import { getResponsiveOverrides } from './themes/responsiveOverrides';

export const billing = createTheme(
  buildTheme({ primaryColor: colors.modules.billing }),
  extendTheme(Application.Billing)
);
export const configuration = createTheme(
  buildTheme({ primaryColor: colors.modules.configuration }),
  extendTheme(Application.Configuration)
);
export const customer = createTheme(
  buildTheme({ primaryColor: colors.modules.customer }),
  extendTheme(Application.CustomerCare)
);
export const documentation = createTheme(
  buildTheme({ primaryColor: colors.modules.documentation }),
  extendTheme(Application.Documentation)
);
export const ratingAndCharging = createTheme(
  buildTheme({ primaryColor: colors.modules.ratingandcharging }),
  extendTheme(Application.RatingAndCharging)
);
export const reporting = createTheme(
  buildTheme({ primaryColor: colors.modules.reporting }),
  extendTheme(Application.Reporting)
);
export const security = createTheme(
  buildTheme({ primaryColor: colors.modules.security }),
  extendTheme(Application.Security)
);
export const studio = createTheme(
  buildTheme({ primaryColor: colors.modules.studio }),
  extendTheme(Application.Studio)
);
export const workbench = createTheme(
  buildTheme({ primaryColor: colors.modules.workbench }),
  extendTheme(Application.Workbench)
);
export const warning = createTheme(
  buildTheme({
    primaryColor: colors.states.warning,
    setDialogBackground: true
  }),
  extendTheme('Warning')
);
export const error = createTheme(
  buildTheme({
    primaryColor: colors.states.error,
    setDialogBackground: true
  }),
  extendTheme('Error')
);

export const success = createTheme(
  buildTheme({
    primaryColor: colors.states.success,
    setDialogBackground: true
  }),
  extendTheme('Success')
);

export const info = createTheme(
  buildTheme({
    primaryColor: colors.states.info,
    setDialogBackground: true
  }),
  extendTheme('Info')
);

export const neutral = createTheme(
  buildTheme({
    primaryColor: colors.modules.neutral,
    setDialogBackground: true
  }),
  extendTheme('Neutral')
);

export const muiThemes = [
  billing,
  configuration,
  customer,
  documentation,
  ratingAndCharging,
  reporting,
  security,
  studio,
  workbench
] as ExtendedTheme[];

interface ExtendedTheme extends Theme {
  name: string;
}

function extendTheme(name: string) {
  return {
    name,
    border: {
      main: standardBorder
    },
    palette: {
      border: {
        main: colors.border.medium,
        light: colors.border.light,
        hover: colors.border.hover,
        dark: colors.border.dark
      },
      canvas: colors.canvas
    },
    overrides: {
      MuiAlert: {
        action: {
          '& .MuiIconButton-root:hover': {
            backgroundColor: 'transparent',
            border: transparentBorder
          }
        },
        root: {
          padding: spacing(1, 3)
        }
      }
    }
  };
}

interface ThemeBuilderProps {
  primaryColor: string;
  setDialogBackground?: boolean;
}

/**
 * Build an MUI theme for a module
 *
 * This function creates a complete MUI theme with:
 * - Base component styles for desktop
 * - Responsive overrides for tablet/touch devices (via CSS media queries)
 *
 * The responsive overrides use CSS media queries (@media (pointer: coarse))
 * to apply touch-optimized styles only on touch devices, ensuring:
 * - Minimum 44px touch targets (WCAG 2.2 / Apple HIG compliant)
 * - 16px font size on inputs (prevents iOS zoom)
 * - Larger checkboxes, radios, and icons for better visibility
 */
function buildTheme({
  primaryColor,
  setDialogBackground
}: ThemeBuilderProps): ThemeOptions {
  // Get responsive overrides for touch devices
  const responsiveOverrides = getResponsiveOverrides(primaryColor);

  const baseTheme: ThemeOptions = {
    palette: {
      border: {},
      canvas: {},
      error: {
        light: rgba(colors.states.error, 0.07),
        main: colors.states.error
      },
      info: {
        light: rgba(colors.states.info, 0.1),
        main: colors.states.info
      },
      primary: {
        main: primaryColor,
        light: rgba(primaryColor, 0.1)
      },
      secondary: {
        main: primaryColor
      },
      success: {
        main: colors.states.success
      },
      text: {
        primary: colors.text.dark,
        secondary: colors.text.light
      },
      warning: {
        main: colors.states.warning
      }
    },
    typography: {
      fontWeightBold: 700,
      fontWeightLight: 300,
      fontWeightMedium: 500,
      fontWeightRegular: 400,
      h1: {
        color: colors.text.dark,
        fontSize: '1.75rem',
        fontWeight: 400,
        letterSpacing: 'normal',
        lineHeight: '2.25rem', //36px
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontFamily: 'Roboto, sans-serif'
      },
      h2: {
        fontSize: '1.125rem', //18px
        lineHeight: '1.4375rem', //23px
        fontWeight: 500,
        color: colors.text.dark
      },
      h3: {
        fontSize: '0.875rem',
        lineHeight: '1rem',
        fontWeight: 600,
        color: colors.text.dark
      },
      h4: {
        fontSize: '0.75rem',
        lineHeight: '0.875rem',
        fontWeight: 700,
        color: colors.text.dark
      },
      subtitle2: {
        fontWeight: 700,
        fontSize: 'inherit',
        padding: '10px 0'
      }
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        }
      },
      MuiCssBaseline: {
        styleOverrides: `
        body {
          align-items: center;
          background-color: #fff;
          color: ${colors.text.dark};
          display: flex;
          font-family: Roboto, sans-serif;
          font-size: 0.875rem;
          font-weight: 400;
          height: 100%;
          justify-content: center;
          line-height: 1.286;
          min-width: 100%;
          overflow-x: hidden;
          width: 100%;
          -webkit-font-smoothing: antialiased
        }

        html {
          font-size: 100%; /* stylelint-disable-line declaration-property-unit-allowed-list*/
          height: 100%;
          width: 100%;
        }

        button, input, optgroup, select, textarea {
          font-family: inherit;
        }

        .c-app {
          width: 100%;
          height: 100%;
          background-color: ${colors.canvas.light};
        }

        *, *::before, *::after {
          box-sizing: initial;
        }
      `
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: '1px solid rgba(0, 0, 0, .15)',
            boxShadow: '0 1px 9px 0 rgba(0, 0, 0, .15)'
          }
        }
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            borderRadius: BASE_SPACING,
            fontSize: '1rem',
            minHeight: 50,
            padding: 0,
            '&.Mui-expanded': {
              minHeight: 50,
              borderRadius: '4px 4px 0 0'
            },
            '&.MuiAccordionSummary-gutters': {
              backgroundColor: colors.canvas.light
            }
          },
          content: {
            margin: 0,
            '&.Mui-expanded': {
              margin: 0
            }
          },
          expanded: {}
        }
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontSize: FONT_SIZE
          }
        }
      },
      // @ts-ignore
      MuiTimelineItem: {
        styleOverrides: {
          root: {
            '&.MuiTimelineItem-missingOppositeContent::before': {
              flex: 0,
              padding: 0
            }
          }
        }
      },
      MuiGrid2: {
        defaultProps: {
          // all grids under this theme will apply
          // negative margin on the top and left sides.
          disableEqualOverflow: true
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.canvas.white,
            borderRadius: BASE_SPACING * 0.5,
            boxShadow: boxShadowTooltip,
            color: colors.text.dark,
            display: 'flex',
            flexDirection: 'column',
            fontSize: '1rem',
            justifyContent: 'center',
            marginLeft: BASE_SPACING * 1.25,
            minHeight: BASE_SPACING * 6,
            minWidth: BASE_SPACING * 40,
            paddingBottom: BASE_SPACING * 3,
            paddingLeft: BASE_SPACING * 4,
            paddingRight: BASE_SPACING * 4,
            paddingTop: BASE_SPACING * 3,
            maxWidth: 'unset'
          },
          arrow: {
            color: colors.canvas.white,
            '&::before': {
              boxShadow: boxShadowTooltipArrow
            }
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            lineHeight: DEFAULT_BUTTON_HEIGHT,
            fontSize: FONT_SIZE,
            fontWeight: 400,
            padding: '0 1em',
            minWidth: 0,
            textTransform: 'none',
            '&+&': {
              marginLeft: '0.5rem'
            },
            variants: [
              {
                props: { variant: 'contained', color: 'secondary' },
                style: {
                  backgroundColor: colors.canvas.medium_light,
                  color: colors.text.dark,
                  '&:hover': {
                    backgroundColor: colors.canvas.medium_light,
                    backgroundImage:
                      'linear-gradient(90deg,rgba(0,0,0,.1),rgba(0,0,0,.1))',
                    boxShadow: 'none'
                  }
                }
              },
              {
                props: { variant: 'contained', color: 'primary' },
                style: {
                  color: colors.canvas.white,
                  backgroundColor: primaryColor,
                  '&:hover': {
                    backgroundColor: primaryColor,
                    backgroundImage:
                      'linear-gradient(90deg,rgba(0,0,0,.1),rgba(0,0,0,.1))'
                  },
                  '&.Mui-disabled': {
                    opacity: 0.5,
                    color: colors.canvas.white,
                    backgroundColor: primaryColor
                  }
                }
              },
              {
                props: { variant: 'contained', color: 'error' },
                style: {
                  backgroundColor: colors.states.error,
                  color: colors.canvas.white,
                  '&:hover': {
                    backgroundColor: colors.states.error,
                    backgroundImage:
                      'linear-gradient(90deg,rgba(0,0,0,.1),rgba(0,0,0,.1))',
                    boxShadow: 'none'
                  }
                }
              },
              {
                props: { variant: 'outlined', color: 'primary' },
                style: {
                  border: standardBorder,
                  '&:hover': {
                    backgroundColor: colors.canvas.light,
                    border: `1px solid ${colors.border.hover}`
                  }
                }
              }
            ]
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          outlined: {
            backgroundColor: 'white',
            padding: '0 1em',
            border: standardBorder,
            boxShadow: boxShadowMedium
          }
        }
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0
          },
          label: {
            marginLeft: BASE_SPACING,
            fontSize: FONT_SIZE
          }
        }
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            padding: 0,
            border: 'none',
            color: colors.canvas.medium,
            '& svg': {
              width: '18px',
              height: '18px'
            },
            '&:hover': {
              backgroundColor: 'transparent',
              border: 'none'
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: BASE_SPACING,
            backgroundColor: colors.canvas.light
          },
          deleteIcon: {
            color: colors.text.light
          },
          sizeSmall: {
            height: BASE_SPACING * 5
          }
        }
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            border: 'none',
            color: colors.canvas.medium,
            padding: 0,
            '& svg': {
              width: '18px',
              height: '18px'
            },
            '&:hover': {
              backgroundColor: 'transparent',
              border: 'none'
            }
          },
          colorSecondary: {
            '&.Mui-disabled': {
              color: colors.canvas.medium_light
            }
          }
        }
      },
      MuiInputAdornment: {
        styleOverrides: {
          positionStart: {
            marginRight: BASE_SPACING
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            paddingTop: 0,
            color: colors.text.dark,
            alignSelf: 'start'
          },
          asterisk: {
            color: colors.states.error
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: 'inherit'
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: 'inherit'
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: BASE_SPACING,
            borderRadius: BASE_SPACING,
            border: transparentBorder,
            fontSize: '1rem',
            '&:hover': {
              backgroundColor: colors.canvas.light,
              border: standardBorder
            }
          },
          colorPrimary: {
            '&:hover': {
              backgroundColor: colors.canvas.light,
              border: standardBorder
            }
          }
        }
      },
      MuiInput: {
        styleOverrides: {
          root: {
            fontSize: 'inherit',

            '&.Mui-focused': {
              borderWidth: '1px'
            }
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          // button: {
          //   '&:hover': {
          //     backgroundColor: 'unset'
          //   }
          // },
          gutters: {
            paddingLeft: BASE_SPACING * 2,
            paddingRight: BASE_SPACING * 2
          },
          root: {
            '&.Mui-focusVisible': {
              backgroundColor: colors.canvas.light
            }
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            paddingTop: BASE_SPACING,
            paddingBottom: BASE_SPACING,
            fontSize: 'inherit',
            lineHeight: 'inherit',
            '&.Mui-selected': {
              backgroundColor: 'transparent',
              color: primaryColor
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            boxShadow: boxShadowInput,

            '&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline':
              {
                borderColor: colors.border.medium
              },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: primaryColor,
              borderWidth: '1px'
            },

            '&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: primaryColor
            }
          },
          adornedStart: {
            paddingLeft: BASE_SPACING * 2
          },
          focused: {},
          input: {
            paddingTop: BASE_SPACING * 1.5,
            paddingLeft: BASE_SPACING * 2,
            paddingBottom: BASE_SPACING * 1.5
          },
          multiline: {
            padding: spacing(1, 2),
            '& > textarea': {
              paddingTop: BASE_SPACING
            }
          },
          notchedOutline: {
            borderColor: colors.border.medium
          }
        }
      },
      MuiPickersSectionList: {
        styleOverrides: {
          section: {
            '[aria-valuetext="Empty"]': {
              color: rgba(colors.text.dark, 0.54)
            }
          }
        }
      },
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            boxShadow: boxShadowInput,
            fontSize: 'inherit',
            padding: spacing(0, 2),
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: primaryColor,
              borderWidth: '1px'
            }
          },
          sectionsContainer: {
            paddingTop: BASE_SPACING * 1.5,
            paddingLeft: BASE_SPACING * 2,
            paddingBottom: BASE_SPACING * 1.5
          }
        }
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 0,

            '&.Mui-error': {
              color: colors.states.error
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paperWidthSm: {
            maxWidth: 640
          },
          paperWidthXs: {
            maxWidth: 510
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            background: setDialogBackground
              ? primaryColor
              : rgba(colors.canvas.light, 0.5),
            color: setDialogBackground ? colors.canvas.white : colors.text.dark,
            padding: '8px 14px'
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: FONT_SIZE
          },
          dividers: {
            padding: FONT_SIZE,
            borderTop: standardBorder,
            borderBottom: standardBorder
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            background: rgba(colors.canvas.light, 0.5)
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            pointerEvents: 'auto'
          }
        }
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            height: '100%'
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-input ~ .MuiSelect-icon': {
              right: `${BASE_SPACING}px`
            }
          },
          icon: {
            fontSize: '1.25rem'
          }
        }
      },
      MuiSnackbar: {
        styleOverrides: {
          anchorOriginTopCenter: {
            marginTop: 20
          }
        }
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            padding: `0 ${BASE_SPACING * 2}px 0 0`,
            width: '622px'
          },
          action: {
            marginRight: 0,
            paddingRight: BASE_SPACING * 2
          },
          message: {
            padding: spacing(1, 0)
          }
        }
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            lineHeight: 1
          }
        }
      },
      MuiTable: {
        styleOverrides: {
          root: {
            display: 'flex',
            flexDirection: 'column'
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            display: 'flex'
          }
        }
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            display: 'flex',
            position: 'relative'
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          alignRight: {
            paddingRight: BASE_SPACING * 6
          },
          root: {
            alignItems: 'center',
            display: 'flex',
            padding: '0 0 0 20px',
            borderBottom: standardBorder,
            overflow: 'hidden'
          },
          stickyHeader: {
            backgroundColor: 'white'
          },
          head: {
            color: colors.text.dark,
            fontWeight: 700,
            fontSize: FONT_SIZE,
            lineHeight: '18px'
          }
        }
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          firstButton: {
            borderRadius: '4px',
            border: '1px solid rgba(0, 0, 0, 0.12)'
          },
          middleButton: {
            borderRadius: '4px',
            border: '1px solid rgba(0, 0, 0, 0.12)'
          },
          lastButton: {
            borderRadius: '4px',
            border: '1px solid rgba(0, 0, 0, 0.12)'
          }
        }
      }
    },
    zIndex: {
      tooltip: 2500
    }
  };

  // Deep merge responsive overrides into the theme
  // This adds touch-device styles via CSS media queries without overwriting base styles
  return deepmerge(baseTheme, { components: responsiveOverrides });
}

function spacing(vertical: number, horizontal: number) {
  return `${BASE_SPACING * vertical}px ${BASE_SPACING * horizontal}px`;
}

/* Full Theme Object
{
   "breakpoints":{
      "keys":[
         "xs",
         "sm",
         "md",
         "lg",
         "xl"
      ],
      "values":{
         "xs":0,
         "sm":600,
         "md":960,
         "lg":1280,
         "xl":1920
      }
   },
   "direction":"ltr",
   "mixins":{
      "toolbar":{
         "minHeight":56,
         "@media (min-width:0px) and (orientation: landscape)":{
            "minHeight":48
         },
         "@media (min-width:600px)":{
            "minHeight":64
         }
      }
   },
   "overrides":{
      "MuiButton":{
         "root":{
            "lineHeight":"18px",
            "padding":"7px",
            "marginLeft":"7px"
         },
         "contained":{
            "boxShadow":"none"
         }
      }
   },
   "palette":{
      "common":{
         "black":"#000",
         "white":"#fff"
      },
      "type":"light",
      "primary":{
         "main":"#36a564",
         "light":"rgb(94, 183, 131)",
         "dark":"rgb(37, 115, 70)",
         "contrastText":"#fff"
      },
      "secondary":{
         "light":"#ff4081",
         "main":"#f50057",
         "dark":"#c51162",
         "contrastText":"#fff"
      },
      "error":{
         "light":"#e57373",
         "main":"#f44336",
         "dark":"#d32f2f",
         "contrastText":"#fff"
      },
      "grey":{
         "50":"#fafafa",
         "100":"#f5f5f5",
         "200":"#eeeeee",
         "300":"#e0e0e0",
         "400":"#bdbdbd",
         "500":"#9e9e9e",
         "600":"#757575",
         "700":"#616161",
         "800":"#424242",
         "900":"#212121",
         "A100":"#d5d5d5",
         "A200":"#aaaaaa",
         "A400":"#303030",
         "A700":"#616161"
      },
      "contrastThreshold":3,
      "tonalOffset":0.2,
      "text":{
         "primary":"rgba(0, 0, 0, 0.87)",
         "secondary":"rgba(0, 0, 0, 0.54)",
         "disabled":"rgba(0, 0, 0, 0.38)",
         "hint":"rgba(0, 0, 0, 0.38)"
      },
      "divider":"rgba(0, 0, 0, 0.12)",
      "background":{
         "paper":"#fff",
         "default":"#fafafa"
      },
      "action":{
         "active":"rgba(0, 0, 0, 0.54)",
         "hover":"rgba(0, 0, 0, 0.08)",
         "hoverOpacity":0.08,
         "selected":"rgba(0, 0, 0, 0.14)",
         "disabled":"rgba(0, 0, 0, 0.26)",
         "disabledBackground":"rgba(0, 0, 0, 0.12)"
      }
   },
   "props":{

   },
   "shadows":[
      "none",
      "0px 1px 3px 0px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 2px 1px -1px rgba(0,0,0,0.12)",
      "0px 1px 5px 0px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 3px 1px -2px rgba(0,0,0,0.12)",
      "0px 1px 8px 0px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 3px 3px -2px rgba(0,0,0,0.12)",
      "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
      "0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
      "0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)",
      "0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)",
      "0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
      "0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)",
      "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
      "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
      "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
      "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
      "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
      "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
      "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
      "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
      "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
      "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
      "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
      "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
      "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
      "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
      "0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)"
   ],
   "typography":{
      "htmlFontSize":16,
      "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
      "fontSize":14,
      "fontWeightLight":300,
      "fontWeightRegular":400,
      "fontWeightMedium":500,
      "fontWeightBold":700,
      "h1":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":300,
         "fontSize":"6rem",
         "lineHeight":1,
         "letterSpacing":"-0.01562em"
      },
      "h2":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":300,
         "fontSize":"3.75rem",
         "lineHeight":1,
         "letterSpacing":"-0.00833em"
      },
      "h3":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":400,
         "fontSize":"3rem",
         "lineHeight":1.04,
         "letterSpacing":"0em"
      },
      "h4":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":400,
         "fontSize":"2.125rem",
         "lineHeight":1.17,
         "letterSpacing":"0.00735em"
      },
      "h5":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":400,
         "fontSize":"1.5rem",
         "lineHeight":1.33,
         "letterSpacing":"0em"
      },
      "h6":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":500,
         "fontSize":"1.25rem",
         "lineHeight":1.6,
         "letterSpacing":"0.0075em"
      },
      "subtitle1":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":400,
         "fontSize":"1rem",
         "lineHeight":1.75,
         "letterSpacing":"0.00938em"
      },
      "subtitle2":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":500,
         "fontSize":"0.875rem",
         "lineHeight":1.57,
         "letterSpacing":"0.00714em"
      },
      "body1":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":400,
         "fontSize":"1rem",
         "lineHeight":1.5,
         "letterSpacing":"0.00938em"
      },
      "body2":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":400,
         "fontSize":"0.875rem",
         "lineHeight":1.43,
         "letterSpacing":"0.01071em"
      },
      "button":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":500,
         "fontSize":"0.875rem",
         "lineHeight":1.75,
         "letterSpacing":"0.02857em",
         "textTransform":"uppercase"
      },
      "caption":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":400,
         "fontSize":"0.75rem",
         "lineHeight":1.66,
         "letterSpacing":"0.03333em"
      },
      "overline":{
         "fontFamily":"\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
         "fontWeight":400,
         "fontSize":"0.75rem",
         "lineHeight":2.66,
         "letterSpacing":"0.08333em",
         "textTransform":"uppercase"
      }
   },
   "shape":{
      "borderRadius":4
   },
   "transitions":{
      "easing":{
         "easeInOut":"cubic-bezier(0.4, 0, 0.2, 1)",
         "easeOut":"cubic-bezier(0.0, 0, 0.2, 1)",
         "easeIn":"cubic-bezier(0.4, 0, 1, 1)",
         "sharp":"cubic-bezier(0.4, 0, 0.6, 1)"
      },
      "duration":{
         "shortest":150,
         "shorter":200,
         "short":250,
         "standard":300,
         "complex":375,
         "enteringScreen":225,
         "leavingScreen":195
      }
   },
   "zIndex":{
      "mobileStepper":1000,
      "appBar":1100,
      "drawer":1200,
      "modal":1300,
      "snackbar":1400,
      "tooltip":1500
   },
   "themeName":"Reporting",
   "name":"Reporting"
}
*/
